import React, { FC, useEffect, useState } from "react";
import {
    DropdownAction,
    Project,
    ProjectMetaData,
    ProjectsData,
    ProjectSettings,
    ProjectVariables,
    UpdateProjectData,
    UserProject,
    UserProjectData,
    UserProjectVariables
} from "../../models/types";
import {
    Card,
    Col,
    Collapse,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { ProjectSettingsModal } from "../modals/ProjectSettingsModal";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { UPDATE_PROJECT, UPDATE_USER_PROJECT } from "../../graphql/mutations";
import {
    GET_PROJECT_META_DATA,
    GET_PROJECTS_BY_PRODUCT,
    GET_USER_PRODUCTS
} from "../../graphql/queries";
import { ActionDropdown } from "../dropdowns/ActionDropdown";
import { Project as ProjectView } from "../views/Project";
import { useLocalAnalyticsState, useLocalState } from "../../graphql/hooks";
import {
    getFormattedTime,
    saveCollapseState,
    updateCollapseState,
    updateCollapseTiersInCache,
    updateProjectFavorites
} from "../../common/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faStar } from "@fortawesome/free-solid-svg-icons";
import { StatusBadges } from "./StatusBadges";

interface Props {
    item: Project;
    userProject?: UserProject;
    productId: number;
    newItemText?: string;
    onCreate?: (variables?: any) => void;
    onRemove: () => void;
}

const ProjectCardItemImplementation: FC<Props> = ({
    item,
    productId,
    userProject,
    onRemove
}) => {
    const client = useApolloClient();
    const state = useLocalState();
    const { analyticsEnabled, analyticsFunnelSelectionMode } =
        useLocalAnalyticsState();
    const [loaded, updateLoaded] = useState(false);
    const [showChildren, updateShowChildren] = useState(
        state.projectTier && state.projectTier.has(Number(item.id))
    );
    const [showSettingsModal, updateShowSettingsModal] = useState(false);

    const { data: { projectMetaData } = {} } = useQuery<
        ProjectMetaData,
        ProjectVariables
    >(GET_PROJECT_META_DATA, {
        variables: { projectId: Number(item?.id) },
        fetchPolicy: "network-only"
    });

    const [updateProjectMutation] = useMutation<
        UpdateProjectData,
        ProjectSettings
    >(UPDATE_PROJECT);

    const [updateUserProject] = useMutation<
        UserProjectData,
        UserProjectVariables
    >(UPDATE_USER_PROJECT);

    useEffect(() => {
        const updatedSet = updateCollapseState(
            state.projectTier,
            Number(item.id),
            showChildren
        );
        saveCollapseState(1, updatedSet);
        updateCollapseTiersInCache(client, undefined, updatedSet);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showChildren]);

    const updateProject = async (setting: ProjectSettings) => {
        try {
            await updateProjectMutation({
                variables: setting,
                update: (cache, { data }) => {
                    const currentProjectsData = cache.readQuery<
                        ProjectsData,
                        ProjectVariables
                    >({
                        query: GET_PROJECTS_BY_PRODUCT,
                        variables: { productId: productId }
                    });
                    const currentProjects = currentProjectsData?.projects;
                    if (currentProjects && data) {
                        const newProjects = currentProjects.map(project =>
                            project.id === data.updateProject.id
                                ? data.updateProject
                                : project
                        );
                        cache.writeQuery<ProjectsData, ProjectVariables>({
                            query: GET_PROJECTS_BY_PRODUCT,
                            data: { projects: newProjects }
                        });
                    }
                }
            });
            updateShowSettingsModal(false);
        } catch (error) {
            console.log("[DEBUG] updateProject error ", error);
        }
    };

    if (!item) {
        return null;
    }

    const actions: DropdownAction[] = [
        {
            header: "Project Settings",
            variant: "inverse",
            callback: () => updateShowSettingsModal(true)
        },
        {
            header: "Remove Project",
            variant: "danger",
            callback: onRemove
        }
    ];

    const favoriteClass = !!userProject ? "text-warning" : "text-disabled";

    const handleFavoriteUpdate = async () => {
        const result = await updateUserProject({
            variables: {
                input: {
                    projectId: Number(item.id),
                    favorite: !userProject
                }
            },
            refetchQueries: [
                {
                    query: GET_USER_PRODUCTS
                }
            ]
        });
        updateProjectFavorites(client, result.data?.userProject, item.id);
    };

    return (
        <Card className="border-0 project-card-background">
            <Card.Header
                className="text-contrast project-title pb-1 mouseHover"
                onClick={e => {
                    if (e.defaultPrevented) return;
                    updateShowChildren(!showChildren);
                }}
            >
                <Row className="d-flex align-items-center no-wrap bread-text">
                    <Col className="text-start ps-2 col-7">
                        <OverlayTrigger
                            delay={{
                                show: 550,
                                hide: 100
                            }}
                            overlay={<Tooltip>Toggle Favorite</Tooltip>}
                        >
                            <FontAwesomeIcon
                                icon={faStar}
                                size="xs"
                                className={`me-1 ${favoriteClass}`}
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleFavoriteUpdate();
                                }}
                            />
                        </OverlayTrigger>
                        <span className="ms-2 text-contrast">{item?.name}</span>
                        <FontAwesomeIcon
                            className={`icon-chevron text-contrast ms-2 ${
                                showChildren ? "rotate-180" : ""
                            }`}
                            icon={faChevronDown}
                        />
                    </Col>
                    <Col className="col-2 d-flex no-wrap text-contrast analytics-font justify-content-center">
                        {getFormattedTime(item.dateUpdated)}
                    </Col>
                    <Col className="text-start ps-3 col-2">
                        {projectMetaData ? (
                            <StatusBadges
                                developerMode={state.developerMode}
                                metaData={projectMetaData}
                            />
                        ) : null}
                    </Col>
                    <Col className="d-flex col-1 justify-content-end p-0">
                        {actions ? (
                            <ActionDropdown
                                id={`project-${item.id}`}
                                variant="success"
                                actions={actions}
                            />
                        ) : null}
                    </Col>
                </Row>
            </Card.Header>
            <Collapse in={showChildren && loaded}>
                <div>
                    <Card.Body className="custom-card-body pe-2 fullSizeChild">
                        <ProjectView
                            project={item}
                            showChildren={showChildren}
                            updateLoaded={updateLoaded}
                        />
                    </Card.Body>
                </div>
            </Collapse>
            <ProjectSettingsModal
                project={item}
                show={showSettingsModal}
                onClose={() => updateShowSettingsModal(false)}
                onSave={updateProject}
            />
        </Card>
    );
};

export const ProjectCardItem = React.memo(ProjectCardItemImplementation);
